import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import SecureStore from '../services/SecureStore';
import * as api from '../services/api';
import { User, LoginCredentials } from '../types';

interface AuthContextType {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    tempToken: string | null; // For 2FA flow
    login: (credentials: LoginCredentials) => Promise<{success: boolean, error?: string, twoFactorRequired?: boolean}>;
    logout: () => void;
    verify2fa: (code: string, tempToken: string) => Promise<{success: boolean, error?: string}>;
    acceptInvitation: (token: string, password: string) => Promise<{success: boolean, error?: string}>;
    completeVerification: (email: string, code: string) => Promise<{success: boolean, error?: string}>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [tempToken, setTempToken] = useState<string | null>(null);

    useEffect(() => {
        // 🔒 SECURITY: Load user credentials from SecureStore only
        // Migration from AsyncStorage was removed in v2.0.0
        const loadUser = async () => {
            try {
                // Load from SecureStore (encrypted storage)
                const secureToken = await SecureStore.getItemAsync('authToken');
                const secureUser = await SecureStore.getItemAsync('userData');

                if (secureToken && secureUser) {
                    setToken(secureToken);
                    setUser(JSON.parse(secureUser));
                }
            } catch (error) {
                console.error('Failed to load credentials', error);
                // Clear any corrupted data
                try {
                    await SecureStore.deleteItemAsync('authToken');
                    await SecureStore.deleteItemAsync('userData');
                    await SecureStore.deleteItemAsync('refreshToken');
                } catch (cleanupError) {
                    console.error('Failed to cleanup secure store', cleanupError);
                }
            } finally {
                setIsLoading(false);
            }
        };
        loadUser();
    }, []);

    const handleLoginSuccess = useCallback(async (data: { user: User, token: string, refreshToken: string }) => {
        // 🔒 SECURITY: Store securely in SecureStore (encrypted)
        await SecureStore.setItemAsync('authToken', data.token);
        await SecureStore.setItemAsync('refreshToken', data.refreshToken ?? '');
        await SecureStore.setItemAsync('userData', JSON.stringify(data.user));

        setUser(data.user);
        setToken(data.token);
        setTempToken(null);
    }, []);

    const login = useCallback(async (credentials: LoginCredentials) => {
        try {
            const data = await api.login(credentials);
            if (data.twoFactorRequired && data.tempToken) {
                setTempToken(data.tempToken);
                return { success: true, twoFactorRequired: true };
            }
            await handleLoginSuccess(data as { user: User, token: string, refreshToken: string });
            return { success: true };
        } catch (error: unknown) {
            // AppError'dan mesajı al, yoksa generic mesaj göster
            let errorMessage = 'Unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message;
                // AppError'un details kısmından mesaj al
                if ((error as any).details?.message) {
                    errorMessage = (error as any).details.message;
                } else if ((error as any).details?.error) {
                    errorMessage = (error as any).details.error;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [handleLoginSuccess]);

    const verify2fa = useCallback(async (code: string, tempToken: string) => {
        try {
            const data = await api.verify2faLogin({ code, tempToken });
            await handleLoginSuccess(data);
            return { success: true };
        } catch (error: unknown) {
            // AppError'dan mesajı al, yoksa generic mesaj göster
            let errorMessage = 'Unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message;
                // AppError'un details kısmından mesaj al
                if ((error as any).details?.message) {
                    errorMessage = (error as any).details.message;
                } else if ((error as any).details?.error) {
                    errorMessage = (error as any).details.error;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [handleLoginSuccess]);
    
    const completeVerification = useCallback(async (email: string, code: string) => {
        try {
            const data = await api.verifyCode({ email, code });
            await handleLoginSuccess(data);
            return { success: true };
        } catch (error: unknown) {
            // AppError'dan mesajı al, yoksa generic mesaj göster
            let errorMessage = 'Unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message;
                // AppError'un details kısmından mesaj al
                if ((error as any).details?.message) {
                    errorMessage = (error as any).details.message;
                } else if ((error as any).details?.error) {
                    errorMessage = (error as any).details.error;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [handleLoginSuccess]);
    
    const acceptInvitation = useCallback(async (invitationToken: string, password: string) => {
        try {
            const data = await api.completeInvitation({ token: invitationToken, password });
            await handleLoginSuccess(data);
            return { success: true };
        } catch(error: unknown) {
            // AppError'dan mesajı al, yoksa generic mesaj göster
            let errorMessage = 'Unknown error occurred';
            if (error instanceof Error) {
                errorMessage = error.message;
                // AppError'un details kısmından mesaj al
                if ((error as any).details?.message) {
                    errorMessage = (error as any).details.message;
                } else if ((error as any).details?.error) {
                    errorMessage = (error as any).details.error;
                }
            }
            return { success: false, error: errorMessage };
        }
    }, [handleLoginSuccess]);

    const logout = useCallback(async () => {
        await AsyncStorage.removeItem('user');
        await SecureStore.deleteItemAsync('authToken');
        await SecureStore.deleteItemAsync('refreshToken');
        await SecureStore.deleteItemAsync('userData');
        setUser(null);
        setToken(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, token, isLoading, tempToken, login, logout, verify2fa, acceptInvitation, completeVerification }}>
            {children}
        </AuthContext.Provider>
    );
};
